/**
 * Server URL management for Capacitor native apps.
 *
 * On native, users enter their Knocknoc server URL on first launch.
 * The URL is persisted via @capacitor/preferences and cached in memory.
 * Dynamic imports keep Preferences out of the web bundle.
 */

import { isNativePlatform } from "./platform";
import { getSessionToken } from "./session-token";
import { BACKGROUND_POLL_INTERVAL } from "../utils/useStatusPolling";

const SERVER_URL_KEY = "knocknoc_server_url";

/**
 * Normalizes a server URL: trims whitespace, prepends https:// if no scheme,
 * and strips trailing slashes.
 *
 * Returns "" for input that cannot be made into a meaningful URL
 * (empty, whitespace only, bare scheme).
 */
export function normalizeServerUrl(url: string): string {
  const trimmed = url.trim();
  if (!trimmed) return "";

  const withScheme = trimmed.startsWith("https://") || trimmed.startsWith("http://") ? trimmed : `https://${trimmed}`;

  const stripped = withScheme.replace(/\/+$/, "");

  // Reject bare scheme like "https:" or "https://" (the latter becomes
  // "https:" after trailing-slash strip).
  if (stripped === "https:" || stripped === "http:") return "";

  return stripped;
}

export type ProbeResult =
  | {
      ok: true;
      normalizedUrl: string;
      buildVersion: string;
      jsBundleHash: string;
    }
  | {
      ok: false;
      reason: "invalid-url" | "timeout" | "unreachable" | "not-knocknoc";
    };

const PROBE_TIMEOUT_MS = 8000;

/**
 * Probes a user-entered server URL to confirm it points at a live Knocknoc
 * server. Hits /api/system_status and validates that the response shape
 * matches the server's systemStatusResponse. Does not persist anything;
 * callers should call setServerUrl(result.normalizedUrl) on success.
 */
export async function probeServerUrl(url: string): Promise<ProbeResult> {
  const normalized = normalizeServerUrl(url);
  if (!normalized) return { ok: false, reason: "invalid-url" };

  let parsedUrl: URL;
  try {
    parsedUrl = new URL(normalized);
  } catch {
    return { ok: false, reason: "invalid-url" };
  }
  if (!parsedUrl.hostname) return { ok: false, reason: "invalid-url" };

  try {
    const res = await fetch(`${normalized}/api/system_status`, {
      method: "GET",
      signal: AbortSignal.timeout(PROBE_TIMEOUT_MS),
    });
    if (!res.ok) return { ok: false, reason: "unreachable" };

    let body: unknown;
    try {
      body = await res.json();
    } catch {
      return { ok: false, reason: "not-knocknoc" };
    }

    if (!isKnocknocSystemStatus(body)) {
      return { ok: false, reason: "not-knocknoc" };
    }

    return {
      ok: true,
      normalizedUrl: normalized,
      buildVersion: body.BuildVersion,
      jsBundleHash: body.JsBundleHash,
    };
  } catch (err) {
    if (err instanceof DOMException && err.name === "TimeoutError") {
      return { ok: false, reason: "timeout" };
    }
    return { ok: false, reason: "unreachable" };
  }
}

type KnocknocSystemStatus = {
  JsBundleHash: string;
  BuildVersion: string;
  MobileSaml: boolean;
};

function isKnocknocSystemStatus(body: unknown): body is KnocknocSystemStatus {
  if (typeof body !== "object" || body === null) return false;
  const b = body as Record<string, unknown>;
  return typeof b.JsBundleHash === "string" && typeof b.BuildVersion === "string" && typeof b.MobileSaml === "boolean";
}

let _cachedUrl: string | null = null;
let _ipChangeStarted = false;
let _ipChangeStarting = false;

export async function getServerUrl(): Promise<string | null> {
  if (!isNativePlatform()) return null;
  if (_cachedUrl) return _cachedUrl;

  const { Preferences } = await import("@capacitor/preferences");
  const { value } = await Preferences.get({ key: SERVER_URL_KEY });
  _cachedUrl = value;
  return value;
}

// The origin to use when building an absolute URL to a server resource. On the
// web that is the page origin (the server itself); on native the WebView origin
// is https://localhost, so use the configured server URL instead.
export async function getServerOrigin(): Promise<string> {
  const url = await getServerUrl();
  if (url) return url.replace(/\/+$/, "");
  return window.location.origin;
}

export async function setServerUrl(url: string): Promise<void> {
  if (!isNativePlatform()) return;

  const normalized = normalizeServerUrl(url);

  const { Preferences } = await import("@capacitor/preferences");
  await Preferences.set({ key: SERVER_URL_KEY, value: normalized });
  _cachedUrl = normalized;

  // Start the native IP change plugin if not already running
  await ensureIpChangeStarted(normalized);
}

/**
 * Ensures the native IpChange plugin is configured and monitoring.
 * Safe to call multiple times; only starts once.
 */
export async function ensureIpChangeStarted(serverUrl?: string): Promise<void> {
  if (!isNativePlatform() || _ipChangeStarted || _ipChangeStarting) return;
  _ipChangeStarting = true;

  try {
    const url = serverUrl ?? (await getServerUrl());
    if (!url) return;

    const { default: IpChange } = await import("./ip-change-plugin");
    await IpChange.configure({
      serverUrl: url,
      pollIntervalSeconds: BACKGROUND_POLL_INTERVAL / 1000,
    });
    // On cold start with an existing logged-in session, seed the plugin
    // with the bearer from Keystore so the foreground-service poller has
    // an Authorization header on its very first tick. Later logins refresh
    // this via session-token.ts.
    const token = await getSessionToken();
    if (token) {
      await IpChange.setSessionToken({ token });
    }
    await IpChange.startMonitoring();
    _ipChangeStarted = true;
  } finally {
    _ipChangeStarting = false;
  }
}

export async function clearServerUrl(): Promise<void> {
  if (!isNativePlatform()) return;
  const { Preferences } = await import("@capacitor/preferences");
  await Preferences.remove({ key: SERVER_URL_KEY });
  _cachedUrl = null;
}
